import { useCallback, useState } from "react";
import { Button } from "@aidkitorg/component-library";
import { StatusFilter } from "./StatusFilter";
import { ChannelFilter } from "./ChannelFilter";
import { useLocalizedStrings } from "../../../../Localization";
import type { NotificationDetails } from "@aidkitorg/types/lib/survey";

export interface NotificationRecipientsFilterParams {
  statuses?: string[];
  channels?: string[];
}

interface NotificationRecipientsFiltersProps {
  onFiltersChange: (filters: NotificationRecipientsFilterParams) => void;
  availableStatuses: string[];
  availableChannels: string[];
  defaultTableColumns?: NotificationDetails['defaultTableColumns'];
}

export const NotificationRecipientsFilters: React.FC<NotificationRecipientsFiltersProps> = ({
  onFiltersChange,
  availableStatuses,
  availableChannels,
  defaultTableColumns,
}) => {
  const L = useLocalizedStrings();

  // Determine which filters to show based on config
  const showStatusFilter = defaultTableColumns?.status === 'visibleAndFilterable';
  const showChannelsFilter = defaultTableColumns?.channel === 'visibleAndFilterable';

  // Local state for managing filters before applying them
  const [localFilters, setLocalFilters] = useState<NotificationRecipientsFilterParams>({
    statuses: [],
    channels: [],
  });

  // Filter handlers
  const handleStatusesChange = useCallback((statuses: string[]) => {
    setLocalFilters({ ...localFilters, statuses });
  }, [localFilters]);

  const handleChannelsChange = useCallback((channels: string[]) => {
    setLocalFilters({ ...localFilters, channels });
  }, [localFilters]);

  // Apply filters function
  const applyFilters = useCallback(() => {
    const cleanFilters = Object.fromEntries(
      Object.entries(localFilters).filter(([_, value]) =>
        Array.isArray(value) ? value.length > 0 : true
      )
    ) as NotificationRecipientsFilterParams;
    onFiltersChange(cleanFilters);
  }, [localFilters, onFiltersChange]);

  // Reset filters function
  const resetFilters = useCallback(() => {
    const emptyFilters = {
      statuses: [],
      channels: [],
    };
    setLocalFilters(emptyFilters);
    onFiltersChange({});
  }, [onFiltersChange]);

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-4">
        {showStatusFilter && (
          <StatusFilter
            selectedStatuses={localFilters.statuses || []}
            availableStatuses={availableStatuses}
            onStatusesChange={handleStatusesChange}
          />
        )}
        {showChannelsFilter && (
          <ChannelFilter
            selectedChannels={localFilters.channels || []}
            availableChannels={availableChannels}
            onChannelsChange={handleChannelsChange}
          />
        )}
      </div>
      <div className="pt-4">
        <div className="flex gap-2">
          <Button variant="secondary" onClick={resetFilters}>
            {L.admin_panel.notifications.reset_filters}
          </Button>
          <Button onClick={applyFilters}>
            {L.admin_panel.notifications.apply_filters}
          </Button>
        </div>
      </div>
    </div>
  );
};
